import { useCallback, useEffect, useRef, useState } from "react";
import {
    cancelTerminalAttempt,
    fetchLatestTerminalAttempt,
    getPaymentsErrorMessage,
    isOpenAttempt,
    startTerminalCharge,
    TerminalAttempt,
} from "./terminalPaymentApi";
import { SplitPayment } from "../split-payment/splitPayments";

const POLL_INTERVAL_MS = 2000;
const COUNTDOWN_TICK_MS = 1000;

export type TerminalPaymentScreen = "loading" | "amount" | "waiting" | "result";

type TerminalPaymentScreenInput = {
    isLoading: boolean;
    isAmountFormOpen: boolean;
    attempt: TerminalAttempt | null;
};

const resolveScreen = ({ isLoading, isAmountFormOpen, attempt }: TerminalPaymentScreenInput) => {
    if (isLoading) return "loading";
    if (isAmountFormOpen || attempt === null) return "amount";
    const screen: TerminalPaymentScreen = isOpenAttempt(attempt) ? "waiting" : "result";
    return screen;
};

export const useTerminalPayment = (orderId: number, onPaid: () => void) => {
    const [attempt, setAttempt] = useState<TerminalAttempt | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isAmountFormOpen, setIsAmountFormOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const notifiedPaidAttemptId = useRef<number | null>(null);

    useEffect(() => {
        let isActive = true;
        fetchLatestTerminalAttempt(orderId)
            .then((latestAttempt) => isActive && setAttempt(latestAttempt))
            .catch((error) => isActive && setErrorMessage(getPaymentsErrorMessage(error)))
            .finally(() => isActive && setIsLoading(false));
        return () => {
            isActive = false;
        };
    }, [orderId]);

    useEffect(() => {
        if (!isOpenAttempt(attempt) || isCancelling) return;
        let isActive = true;
        const pollTimer = setTimeout(() => {
            fetchLatestTerminalAttempt(orderId)
                .then((latestAttempt) => isActive && setAttempt(latestAttempt))
                .catch(() => isActive && setAttempt((currentAttempt) => (currentAttempt ? { ...currentAttempt } : currentAttempt)));
        }, POLL_INTERVAL_MS);
        return () => {
            isActive = false;
            clearTimeout(pollTimer);
        };
    }, [attempt, isCancelling, orderId]);

    const isAttemptOpen = isOpenAttempt(attempt);
    const serverSecondsRemaining = attempt?.seconds_remaining ?? 0;

    useEffect(() => {
        setSecondsRemaining(serverSecondsRemaining);
        if (!isAttemptOpen) return;
        const countdownTimer = setInterval(() => {
            setSecondsRemaining((currentSeconds) => Math.max(0, currentSeconds - 1));
        }, COUNTDOWN_TICK_MS);
        return () => clearInterval(countdownTimer);
    }, [isAttemptOpen, serverSecondsRemaining]);

    useEffect(() => {
        const isRecordedPayment = attempt?.status === "paid" && attempt.recording_status === "recorded";
        if (!attempt || !isRecordedPayment || notifiedPaidAttemptId.current === attempt.id) return;
        notifiedPaidAttemptId.current = attempt.id;
        onPaid();
    }, [attempt, onPaid]);

    const startCharge = useCallback(
        async (splitPayments: Array<SplitPayment>) => {
            setIsSubmitting(true);
            setErrorMessage(null);
            try {
                const startedAttempt = await startTerminalCharge(orderId, splitPayments);
                setAttempt(startedAttempt);
                setIsAmountFormOpen(false);
            } catch (error) {
                const runningAttempt = await fetchLatestTerminalAttempt(orderId).catch(() => null);
                if (isOpenAttempt(runningAttempt)) {
                    setAttempt(runningAttempt);
                    setIsAmountFormOpen(false);
                } else {
                    setErrorMessage(getPaymentsErrorMessage(error));
                }
            } finally {
                setIsSubmitting(false);
            }
        },
        [orderId],
    );

    const cancelCharge = useCallback(async () => {
        if (!attempt) return;
        setIsCancelling(true);
        setErrorMessage(null);
        try {
            const cancelledAttempt = await cancelTerminalAttempt(attempt.id, "operator");
            setAttempt(cancelledAttempt);
        } catch (error) {
            setErrorMessage(getPaymentsErrorMessage(error));
        } finally {
            setIsCancelling(false);
        }
    }, [attempt]);

    const openAmountForm = useCallback(() => {
        setErrorMessage(null);
        setIsAmountFormOpen(true);
    }, []);

    const screen = resolveScreen({ isLoading, isAmountFormOpen, attempt });
    const terminalPayment = {
        screen,
        attempt,
        secondsRemaining,
        isSubmitting,
        isCancelling,
        errorMessage,
        startCharge,
        cancelCharge,
        openAmountForm,
    };
    return terminalPayment;
};
